import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Btn } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DOMPurify from "dompurify";
import { Rocket, Send, Mail } from "lucide-react";

export function LaunchEmailDialog({ open, onClose }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSent(null);
    api.get("/crew/launch-email/preview").then((r) => setPreview(r.data)).catch(() => toast.error("Could not load preview"));
  }, [open]);

  const send = async (testOnly) => {
    if (!testOnly && !window.confirm(`Send the launch announcement to ${preview?.recipients ?? 0} contractors now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/crew/launch-email", { test_only: testOnly });
      setSent(data);
      toast.success(testOnly ? "Test email sent to your inbox" : `Launch email is sending to ${data.emailable ?? data.recipients} contractors now — rolling out over the next minute`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="launch-email-dialog">
        <DialogHeader><DialogTitle className="font-head font-black uppercase flex items-center gap-2"><Rocket size={18} className="text-[#FF5722]" /> Launch Announcement</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">Step-by-step email telling every contractor the app is live: how to sign in, set coverage areas, stay Available, allow notifications, receive and work orders, and message the SSS team. Includes the app link, training video and PDF guide.</p>
          {preview && (
            <>
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="bg-zinc-800 border border-black text-zinc-300 px-2 py-1" data-testid="launch-recipients">{preview.recipients} contractors with a valid email</span>
                <span className="bg-zinc-800 border border-black text-zinc-300 px-2 py-1">Subject: {preview.subject}</span>
              </div>
              <div className="border-2 border-zinc-800 bg-white max-h-[45vh] overflow-y-auto" data-testid="launch-preview">
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html, { ADD_ATTR: ["target"] }) }} />
              </div>
            </>
          )}
          <div className="flex gap-2 flex-wrap">
            <Btn variant="secondary" data-testid="launch-send-test-btn" onClick={() => send(true)} disabled={busy || !preview}><Mail size={16} /> Send me a test</Btn>
            <Btn data-testid="launch-send-all-btn" onClick={() => send(false)} disabled={busy || !preview} className="flex-1"><Send size={16} /> {busy ? "Sending…" : "Send to all contractors"}</Btn>
          </div>
          {sent && !sent.test && <div data-testid="launch-sent-result" className="font-mono text-xs text-green-300">Emailing {sent.emailable ?? sent.recipients} contractors now — sends roll out over the next minute. In-app notification posted to everyone.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
